import {
  forwardRef,
  useCallback,
  useEffect,
  useRef,
  useState,
  type HTMLAttributes,
  type TableHTMLAttributes,
  type TdHTMLAttributes,
  type ThHTMLAttributes,
} from 'react';
import { cn } from '../lib/cn';
import { focusRing } from '../lib/focus-ring';

/* -------------------------------------------------------------------------- */
/* shared                                                                      */
/* -------------------------------------------------------------------------- */

const alignClass = {
  left: 'text-left',
  center: 'text-center',
  right: 'text-right',
} as const;

export type TableAlign = keyof typeof alignClass;

/* -------------------------------------------------------------------------- */
/* container                                                                   */
/* -------------------------------------------------------------------------- */

/**
 * True while the element's content is wider than the element itself. Used to
 * decide whether the scroll region needs to be keyboard focusable: a table that
 * fits should not add a tab stop, one that scrolls must be reachable by
 * keyboard alone (WCAG 2.1.1).
 */
function useHorizontalOverflow(ref: { current: HTMLElement | null }): boolean {
  const [overflowing, setOverflowing] = useState(false);

  useEffect(() => {
    const element = ref.current;
    if (element === null) return;

    const measure = () => {
      // +1 absorbs sub-pixel rounding, which would otherwise report a table
      // that fits exactly as scrollable.
      setOverflowing(element.scrollWidth > element.clientWidth + 1);
    };

    measure();

    if (typeof ResizeObserver === 'undefined') return;

    const observer = new ResizeObserver(measure);
    observer.observe(element);
    const content = element.firstElementChild;
    if (content !== null) observer.observe(content);

    return () => {
      observer.disconnect();
    };
  }, [ref]);

  return overflowing;
}

export interface TableContainerProps extends HTMLAttributes<HTMLDivElement> {
  /**
   * Accessible name for the scroll region. Supply it whenever the table can be
   * wider than its container — without a name the region is anonymous to a
   * screen reader.
   */
  label?: string;
  /** Set false when the parent already draws the frame (see `DataTable`). */
  bordered?: boolean;
}

/**
 * The only thing that may scroll sideways. A wide table scrolls *inside* this
 * box so the page body never gains a horizontal scrollbar — which is why it
 * also carries `min-w-0`: without it a flex or grid child refuses to shrink
 * below its content and pushes the whole page wide instead.
 */
export const TableContainer = forwardRef<HTMLDivElement, TableContainerProps>(
  function TableContainer({ className, label, bordered = true, children, ...rest }, ref) {
    const innerRef = useRef<HTMLDivElement | null>(null);
    const overflowing = useHorizontalOverflow(innerRef);

    const setRefs = useCallback(
      (node: HTMLDivElement | null) => {
        innerRef.current = node;
        if (typeof ref === 'function') ref(node);
        else if (ref !== null && ref !== undefined) ref.current = node;
      },
      [ref],
    );

    return (
      <div
        ref={setRefs}
        // Only a scrollable region becomes focusable, and only a named one
        // claims role="region" — an unnamed landmark is noise.
        tabIndex={overflowing ? 0 : undefined}
        role={overflowing && label !== undefined ? 'region' : undefined}
        aria-label={overflowing && label !== undefined ? label : undefined}
        className={cn(
          'w-full min-w-0 max-w-full overflow-x-auto',
          bordered && 'rounded-lg border border-line bg-white/[0.02]',
          focusRing,
          className,
        )}
        {...rest}
      >
        {children}
      </div>
    );
  },
);

/* -------------------------------------------------------------------------- */
/* table                                                                       */
/* -------------------------------------------------------------------------- */

export interface TableProps extends TableHTMLAttributes<HTMLTableElement> {
  /**
   * Width below which the table stops shrinking and the container scrolls.
   * Any CSS length; defaults to `640px`, matching the design language.
   */
  minWidth?: string;
}

export const Table = forwardRef<HTMLTableElement, TableProps>(function Table(
  { className, minWidth = '640px', style, ...rest },
  ref,
) {
  return (
    <table
      ref={ref}
      className={cn('w-full border-collapse text-[0.9rem]', className)}
      style={{ minWidth, ...style }}
      {...rest}
    />
  );
});

export type TableSectionProps = HTMLAttributes<HTMLTableSectionElement>;

// `thead` and `tr` carry no base classes of their own — the treatment lives on
// the cells and on `TableBody` — so they pass `className` straight through
// rather than emitting an empty `class` attribute.
export const TableHeader = forwardRef<HTMLTableSectionElement, TableSectionProps>(
  function TableHeader({ className, ...rest }, ref) {
    return <thead ref={ref} className={className} {...rest} />;
  },
);

export const TableBody = forwardRef<HTMLTableSectionElement, TableSectionProps>(
  function TableBody({ className, ...rest }, ref) {
    return (
      <tbody
        ref={ref}
        className={cn(
          // Hover and the trailing hairline live here rather than on TableRow so
          // that header rows are not caught by them.
          '[&>tr]:transition-colors [&>tr]:duration-150 [&>tr:hover]:bg-white/[0.025]',
          '[&>tr:last-child>td]:border-b-0',
          className,
        )}
        {...rest}
      />
    );
  },
);

export type TableRowProps = HTMLAttributes<HTMLTableRowElement>;

export const TableRow = forwardRef<HTMLTableRowElement, TableRowProps>(function TableRow(
  { className, ...rest },
  ref,
) {
  return <tr ref={ref} className={className} {...rest} />;
});

export interface TableHeaderCellProps extends ThHTMLAttributes<HTMLTableCellElement> {
  align?: TableAlign;
}

/**
 * The mono-uppercase header treatment the whole Wend design language uses for
 * data chrome — same voice as `Label` and the sidebar eyebrows.
 */
export const TableHeaderCell = forwardRef<HTMLTableCellElement, TableHeaderCellProps>(
  function TableHeaderCell({ className, align = 'left', scope = 'col', ...rest }, ref) {
    return (
      <th
        ref={ref}
        scope={scope}
        className={cn(
          'border-b border-line bg-white/[0.03] px-4 py-3 align-middle whitespace-nowrap',
          'font-mono text-[0.7rem] font-medium uppercase leading-none tracking-[0.11em] text-muted',
          alignClass[align],
          className,
        )}
        {...rest}
      />
    );
  },
);

export interface TableCellProps extends TdHTMLAttributes<HTMLTableCellElement> {
  align?: TableAlign;
}

export const TableCell = forwardRef<HTMLTableCellElement, TableCellProps>(function TableCell(
  { className, align = 'left', ...rest },
  ref,
) {
  return (
    <td
      ref={ref}
      className={cn(
        'border-b border-line px-4 py-3 align-top leading-snug text-text',
        alignClass[align],
        className,
      )}
      {...rest}
    />
  );
});
